// 2P AI API client. snake_case end to end (matches docs/api.md). Session cookie is
// HttpOnly (set by the server); the CSRF token returned at login is kept in sessionStorage
// and sent as X-CSRF on every state-changing request. Errors surface the {error:{code,message,details}} envelope.

const BASE = '/api';
const CSRF_KEY = 'p2ai_csrf';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getCsrf(): string | null {
  return sessionStorage.getItem(CSRF_KEY);
}

export function setCsrf(token: string | null): void {
  if (token) sessionStorage.setItem(CSRF_KEY, token);
  else sessionStorage.removeItem(CSRF_KEY);
}

type JsonObject = Record<string, unknown>;

async function request<T>(method: string, path: string, body?: unknown, stepUpToken?: string): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = { method, credentials: 'same-origin', headers };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (method !== 'GET') {
    const csrf = getCsrf();
    if (csrf) headers['X-CSRF'] = csrf;
  }
  if (stepUpToken) headers['X-StepUp'] = stepUpToken;

  const res = await fetch(BASE + path, init);
  if (res.status === 204) return undefined as T;

  const data = (await res.json().catch(() => null)) as JsonObject | null;
  if (!res.ok) {
    const err = (data?.error ?? {}) as JsonObject;
    throw new ApiError(
      res.status,
      String(err.code ?? 'error'),
      String(err.message ?? res.statusText),
      err.details,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown, stepUp?: string): Promise<T> => request<T>('POST', path, body, stepUp),
  patch: <T>(path: string, body?: unknown, stepUp?: string): Promise<T> => request<T>('PATCH', path, body, stepUp),
  del: <T>(path: string, stepUp?: string): Promise<T> => request<T>('DELETE', path, undefined, stepUp),
};
